import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';

interface StatCardProps {
  Icon: LucideIcon;
  label: string;
  value: number | string;
  subtext?: string;
  linkTo?: string;
  loading?: boolean;
}

export const StatCard: React.FC<StatCardProps> = ({ Icon, label, value, subtext, linkTo, loading }) => {
  const [isHovered, setIsHovered] = useState(false);

  const cardBaseStyle: React.CSSProperties = {
    backgroundColor: 'var(--background-color)',
    border: '1px solid var(--border-color)',
    borderRadius: 'var(--radius)',
    padding: '0.625rem',
    boxShadow: 'var(--shadow)',
    cursor: linkTo ? 'pointer' : 'default',
    transition: 'all 0.2s ease-in-out',
    textDecoration: 'none',
    display: 'block',
    minHeight: '80px',
  };

  const cardHoverStyle: React.CSSProperties = linkTo
    ? {
        transform: 'translateY(-2px)',
        boxShadow: 'var(--shadow-lg)',
      }
    : {};

  const cardStyle: React.CSSProperties = {
    ...cardBaseStyle,
    ...(isHovered ? cardHoverStyle : {}),
  };

  const loadingSkeletonStyle: React.CSSProperties = {
    backgroundColor: 'var(--border-color)',
    borderRadius: 'var(--radius)',
    animation: 'pulse 1.5s ease-in-out infinite',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '0.375rem',
  };

  const iconStyle: React.CSSProperties = {
    opacity: loading ? 0.3 : 1,
    display: 'flex',
    alignItems: 'center',
    color: 'var(--primary-color)',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '0.85rem',
    color: 'var(--text-secondary)',
    fontWeight: '500',
    lineHeight: 1,
  };

  const valueStyle: React.CSSProperties = {
    fontSize: '1.35rem',
    fontWeight: '600',
    margin: '0.1875rem 0',
    color: 'var(--text-primary)',
    lineHeight: '1.2',
    textAlign: 'center',
  };

  const subtextStyle: React.CSSProperties = {
    fontSize: '0.625rem',
    color: 'var(--text-secondary)',
    marginTop: '0.1875rem',
    lineHeight: '1.3',
    textAlign: 'center',
  };

  const content = (
    <div>
      <div style={headerStyle}>
        <div style={iconStyle}>
          <Icon size={20} aria-hidden="true" />
        </div>
        <div style={labelStyle}>{label}</div>
      </div>
      {loading ? (
        <>
          <div
            style={{
              ...loadingSkeletonStyle,
              height: '1.25rem',
              width: '60%',
              marginBottom: '0.1875rem',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          />
          {subtext && (
            <div
              style={{
                ...loadingSkeletonStyle,
                height: '0.625rem',
                width: '80%',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            />
          )}
        </>
      ) : (
        <>
          <div style={valueStyle}>{value}</div>
          {subtext && <div style={subtextStyle}>{subtext}</div>}
        </>
      )}
    </div>
  );

  if (linkTo) {
    return (
      <Link
        to={linkTo}
        style={cardStyle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {content}
      </Link>
    );
  }

  return <div style={cardStyle}>{content}</div>;
};
